import { memo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { MoreHorizontal } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import type { WrapUpField } from "@/hooks/useEventsPipeline";
import type { EventBoardHandlers } from "@/hooks/useEventBoardActions";
import { fmtMoney, type BoardRow } from "@/lib/event-board";
import { quoteStatusLabel } from "@/lib/event-pipeline";
import { cn } from "@/lib/utils";
import { EventFlags, QuoteStatusDot } from "../EventFlags";
import { PostEventChecklist } from "./PostEventChecklist";

interface EventPipelineCardProps {
  row: BoardRow;
  handlers: EventBoardHandlers;
  /** The Mark sent field saving for this event, if any. */
  pendingField: WrapUpField | null;
  donePending: boolean;
  addQuotePending: boolean;
  wrapUpUnavailable: boolean;
}

/** The quote line: the primary quote (opens in the side panel), "+N" more, and its total. */
function QuoteLine({ row, onOpen }: { row: BoardRow; onOpen: (quoteId: string) => void }) {
  const q = row.quote;
  return (
    <div className="flex items-center justify-between gap-2 border-t pt-[7px] text-[12.5px] tabular-nums">
      {q ? (
        <button
          type="button"
          onClick={() => onOpen(q.id)}
          aria-label={`Open quote ${q.quote_number} v${row.quoteVersion ?? 1} (${quoteStatusLabel(q)})`}
          className="inline-flex min-w-0 items-center gap-[5px] text-left font-semibold hover:underline"
        >
          <QuoteStatusDot status={row.quoteStatus} />
          <span>
            <span className="whitespace-nowrap">{q.quote_number}</span>{" "}
            <span className="whitespace-nowrap">· {row.quoteStatusLabel}</span>
            {row.otherQuotes > 0 && <span className="font-medium text-muted-foreground"> +{row.otherQuotes}</span>}
          </span>
        </button>
      ) : (
        <span className="inline-flex items-center gap-[5px] font-semibold">
          <QuoteStatusDot status="none" />
          No quote yet
        </span>
      )}
      <b className="shrink-0 whitespace-nowrap">{q ? fmtMoney(row.total) : ""}</b>
    </div>
  );
}

/** One event on the board (#1557): opens the event page; no dragging. */
export const EventPipelineCard = memo(function EventPipelineCard({
  row,
  handlers,
  pendingField,
  donePending,
  addQuotePending,
  wrapUpUnavailable,
}: EventPipelineCardProps) {
  const navigate = useNavigate();
  const open = useCallback(() => navigate(`/admin/events/${row.id}`), [navigate, row.id]);

  return (
    <article
      aria-label={row.name}
      className={cn(
        "grid gap-[7px] rounded-[9px] border bg-card px-3 py-2.5 shadow-[0_1px_2px_hsl(30_20%_20%/.06),0_4px_14px_hsl(30_20%_20%/.06)] hover:border-accent",
        row.alert && "border-l-[3px] border-l-[hsl(var(--ev-hot))] hover:border-l-[hsl(var(--ev-hot))]",
      )}
    >
      <div className="flex items-start gap-1">
        <button type="button" onClick={open} className="grid min-w-0 flex-1 gap-[3px] p-0 text-left">
          <h4 className="text-sm font-semibold">
            {row.name}
            {row.occasion && <span className="font-medium text-muted-foreground"> · {row.occasion}</span>}
          </h4>
          <span className="text-[12.5px] tabular-nums text-muted-foreground">
            {row.dateText} · {row.guests} guests · {row.serviceLabel}
          </span>
        </button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              aria-label={`Actions for ${row.name}`}
              className="-mr-1 -mt-0.5 grid h-6 w-6 shrink-0 place-items-center rounded text-muted-foreground hover:bg-muted"
            >
              <MoreHorizontal className="h-4 w-4" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem disabled={addQuotePending} onSelect={() => handlers.addQuote(row.id)}>
              Add quote
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={() => handlers.markLost(row)}>Mark lost</DropdownMenuItem>
            <DropdownMenuItem onSelect={() => handlers.cancelEvent(row)}>Cancel event</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      <EventFlags flags={row.flags} wrapText />
      <QuoteLine row={row} onOpen={handlers.openQuote} />
      {row.stage === "post_event" && (
        <PostEventChecklist
          row={row}
          handlers={handlers}
          pendingField={pendingField}
          donePending={donePending}
          wrapUpUnavailable={wrapUpUnavailable}
        />
      )}
    </article>
  );
});
